import {parse, stringify} from 'yaml'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {DataObjectId} from '../data-object'
import {WorkflowConfig} from './conf-interfaces'
import {Field} from './engine'
import {findIdInWorkflow, WorkflowIds} from './workflow-ids'

export interface WorkflowHistoryItem {
  actionId: string
  userId: DataObjectId
}

export interface WorkflowState {
  id: string
  stageId: string
  fields?: {[id: string]: unknown}
  history: {[time: number]: WorkflowHistoryItem}
}

export interface Workflow {
  id: string
  name: string
  boards: DataObjectId[]
  config: string
  ids: WorkflowIds
}

export const makeNewWorkflow = (): Workflow => {
  return {
    id: Util.longId(),
    name: '',
    boards: [],
    config: '',
    ids: {}
  }
}

export function workflowToConfig(workflow: Workflow): WorkflowConfig
export function workflowToConfig(
  workflow: Workflow | undefined
): WorkflowConfig | undefined {
  return workflow ? parse(workflow.config) : undefined
}

const getWorkflowFieldDefault = (field: Field): '' | 0 | [] => {
  switch (field.type) {
    case 'text':
      return ''
    case 'number':
      return 0
    case 'users':
      return []
    default:
      throw new Error('Not implemented field types')
  }
}

export const getWorkflowFieldFromState = (
  workflow: Workflow,
  workflowState: WorkflowState,
  field: Field
): string[] | number | string => {
  const id = findIdInWorkflow(workflow.ids, 'fields', field.name)
  const defaultValue = getWorkflowFieldDefault(field)
  if (!id) {
    return defaultValue
  }
  const value = workflowState.fields?.[id]
  if (value === undefined) {
    return defaultValue
  }
  if (field.type === 'text') {
    return String(value)
  } else if (field.type === 'number') {
    return Number(value)
  } else if (field.type === 'users') {
    return value as DataObjectId[]
  } else {
    throw new Error('Not implemented field types')
  }
}

export const getDefaultWorkflow = (columnNames: string[]): string => {
  const config: WorkflowConfig = {
    name: Lang.msg.workflow.workflowDefaultName,
    restrict: ['create', 'location'],
    create: {
      actions: [
        {
          name: Lang.msg.workflow.create,
          effect: [{stage: columnNames[0]}, {location: columnNames[0]}]
        }
      ]
    },
    stages: columnNames.map((c, ix) => {
      const nextCol = columnNames[ix + 1]
      return {
        name: c,
        actions: [
          nextCol
            ? {
                name: Lang.msg.workflow.nextStageAction,
                effect: [{stage: nextCol}, {location: nextCol}]
              }
            : {name: Lang.msg.workflow.nextStageAction}
        ]
      }
    })
  }
  return stringify(config)
}
